import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { Book, Plus, ShieldAlert } from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/menus/empty")({
  component: MenusEmptyState,
});

function MenusEmptyState() {
  const navigate = useNavigate();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canEditMenus = hasPermission("editMenus");

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to edit menus
  if (!canEditMenus) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to manage menus. Contact your manager to
            request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <>
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Book />
          </EmptyMedia>
          <EmptyTitle>Create a menu to get started</EmptyTitle>
          <EmptyDescription>
            Create menus to enable ordering food and drinks in POS
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {/* V4.1: Route-based create for URL context tracking */}
          <Button
            onClick={() => navigate({ to: "/menus/new" })}
            size="lg"
            type="button"
          >
            <Plus />
            Create Menu
          </Button>
        </EmptyContent>
      </Empty>
      {/* Outlet for /menus/new modal */}
      <Outlet />
    </>
  );
}
